import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import classNames from 'classnames';
import { useDrag } from 'react-dnd';
import { DndType } from 'src/types/dnd';
import Deadline from 'src/pages/TendersPage/TendersList/Deadline';
import { TenderStatusBadge, Tooltip, UserName } from 'src/common';
import { ActivePreviewTab } from 'src/models/procurements/types';
import { useOpenSidebar, useCurrentSidebar } from 'src/shared/InfoSidebar/hooks';
import { SidebarMode } from 'src/shared/InfoSidebar/types';
import { usePreviewTabState } from 'src/models/procurements/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots } from '@fortawesome/pro-light-svg-icons';
import { IBids } from 'src/models/bids/Bids/types';
import ReadOnlyBlockCurrency from 'src/shared/Blocks/Currency/ReadOnlyBlock';
import BaseBadge from 'src/common/BaseBadge';
import { trackOpenBidPreviewInWorkspace } from 'src/segment/events';

interface IProps {
  bid: IBids;
  idx: number;
}

export const BoardItem: FC<IProps> = props => {
  const { bid, idx } = props;
  const { origin, id, assignedTo, isCommented, isRejected } = bid;
  const { deadline, estimatedValue, procurerTitle, name, procurementStatus } = origin;
  const { t } = useTranslation();
  const openSidebar = useOpenSidebar();
  const currentOpenBid = useCurrentSidebar();

  const [, setActivePreviewTab] = usePreviewTabState();
  const [{ isDragging }, drag] = useDrag({
    item: { type: DndType.BidsBoardItem, dndItem: bid, itemIds: [], index: idx },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  const onGoToPreview = useCallback(
    e => {
      e.preventDefault();
      openSidebar({
        id,
        mode: SidebarMode.BID_INFO
      });
      trackOpenBidPreviewInWorkspace(
        {
          id: bid.workspace.id
        },
        bid.status?.category
      );
    },
    [id, openSidebar, bid]
  );

  const onGoToBidComments = useCallback(
    e => {
      e.preventDefault();
      openSidebar({
        id,
        mode: SidebarMode.BID_INFO
      });
      setActivePreviewTab(ActivePreviewTab.Comments);
    },
    [id, openSidebar, setActivePreviewTab]
  );

  return (
    <li
      className={classNames(styles.item, {
        [styles.isDragging]: isDragging,
        [styles.isHighlighted]: currentOpenBid && currentOpenBid.id === id
      })}
      ref={drag}
    >
      <div className={styles.itemInner}>
        <Tooltip title={name}>
          <button className={styles.title} onClick={onGoToPreview}>
            <span>{name}</span>
          </button>
        </Tooltip>
        <ul className={styles.infoList}>
          {estimatedValue && (
            <li className={styles.infoItem}>
              <ReadOnlyBlockCurrency
                className={styles.currencyBlock}
                amount={estimatedValue.amount}
                currency={estimatedValue.currency}
                config={{ currency: { isShort: true } }}
              />
            </li>
          )}
          <li className={classNames(styles.infoItem, styles.deadline)}>
            {t('Tenders.deadline')}: <Deadline deadline={deadline} />
          </li>
          <li className={classNames(styles.infoItem, styles.assignedTo)}>
            {assignedTo && <UserName user={assignedTo} mode={'onlyName'} size={'xs'} className={styles.userName} />}
            {!!procurerTitle && (
              <Tooltip title={procurerTitle}>
                <div className={styles.procurerTitle}>{procurerTitle}</div>
              </Tooltip>
            )}
          </li>
        </ul>
        <div className={styles.itemActions}>
          {isRejected && <BaseBadge title={t('BidSpaces.rejected')} theme={'rejected'} />}
          <ul className={styles.itemActionsContainer}>
            {procurementStatus && (
              <TenderStatusBadge
                mode="icon"
                className={styles.tenderStatusBadge}
                statuses={procurementStatus}
                wrapper={({ children }) => <li className={styles.itemAction}>{children}</li>}
              />
            )}

            {isCommented && (
              <li className={styles.itemAction}>
                <button className={styles.commentsButton} onClick={onGoToBidComments}>
                  <FontAwesomeIcon icon={faCommentDots} />
                </button>
              </li>
            )}
          </ul>
        </div>
      </div>
    </li>
  );
};
export default BoardItem;
